import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from './skeleton'

interface OptimizedImageProps {
  src: string
  alt: string
  className?: string
  sizes?: string
  priority?: boolean
  fallbackSrc?: string
  onLoad?: () => void
  onError?: () => void
}

export const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  priority = false,
  fallbackSrc = '/assets/graphics/placeholder-image.jpg',
  onLoad,
  onError
}: OptimizedImageProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const [currentSrc, setCurrentSrc] = useState(src)

  const handleLoad = () => {
    setIsLoading(false)
    onLoad?.()
  }

  const handleError = () => {
    setIsLoading(false)
    if (!error && currentSrc !== fallbackSrc) {
      setError(true)
      setCurrentSrc(fallbackSrc)
    } else {
      setError(true)
    }
    onError?.()
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {isLoading && (
        <div className="absolute inset-0 z-10">
          <Skeleton className="w-full h-full" />
        </div>
      )}
      
      <img
        src={currentSrc}
        alt={alt}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          "transition-opacity duration-300",
          isLoading ? "opacity-0" : "opacity-100"
        )}
      />
      
      {error && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 mb-2 text-2xl">📄</div>
            <span className="text-gray-500 text-sm">Failed to load image</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Responsive image component
export const ResponsiveImage = ({ 
  src, 
  alt, 
  className,
  aspectRatio = '16/9',
  ...props 
}: OptimizedImageProps & { aspectRatio?: string }) => {
  return (
    <div 
      className={cn("relative", className)}
      style={{ aspectRatio }}
    >
      <OptimizedImage
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        {...props}
      />
    </div>
  )
} 